'use client';

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Icon } from '../../icons';
import { Button } from '../Button/Button';
import { Toggle } from '../Toggle/Toggle';
import { InputSelect } from '../InputSelect/InputSelect';
import type { DisplaySettings, SortConfig, GroupConfig, ColumnConfig } from '@/types/tableViews';

import './DisplayPanel.css';

export interface DisplayPanelToggleOption {
  key: string;
  label: string;
  checked: boolean;
}

export interface DisplayPanelProps {
  open: boolean;
  onClose: () => void;
  displaySettings: DisplaySettings;
  onUpdateSort: (sort: SortConfig) => void;
  onUpdateGroup: (group: GroupConfig) => void;
  onToggleColumn: (columnKey: string) => void;
  onReset: () => void;
  onSave: () => void;
  isDefaultView?: boolean;
  sortableColumns?: { key: string; label: string }[];
  groupableColumns?: { key: string; label: string }[];
  toggleOptions?: DisplayPanelToggleOption[];
  onToggleOption?: (key: string) => void;
  hiddenColumnKeys?: string[];
  reorderableColumnKeys?: string[];
  onReorderColumn?: (columnKey: string, overColumnKey: string) => void;
  columnsSectionTitle?: string;
  hideSortSection?: boolean;
  onSaveAsNew?: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

export function DisplayPanel({
  open,
  onClose,
  displaySettings,
  onUpdateSort,
  onUpdateGroup,
  onToggleColumn,
  onReset,
  onSave,
  isDefaultView = false,
  sortableColumns = [],
  groupableColumns = [],
  toggleOptions,
  onToggleOption,
  hiddenColumnKeys,
  reorderableColumnKeys,
  onReorderColumn,
  columnsSectionTitle = 'Display Columns',
  hideSortSection = false,
  onSaveAsNew,
  anchorRef,
  className = '',
}: DisplayPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const visibleColumns = displaySettings.columns.filter(
    (col) => !hiddenColumnKeys?.includes(col.key),
  );
  const reorderableSet = reorderableColumnKeys
    ? new Set(reorderableColumnKeys)
    : null;
  const reorderableColumns = visibleColumns.filter((col) =>
    reorderableSet ? reorderableSet.has(col.key) : true,
  );

  const getMoveTarget = (columnKey: string, direction: -1 | 1) => {
    const index = reorderableColumns.findIndex((col) => col.key === columnKey);
    if (index < 0) return null;
    return reorderableColumns[index + direction]?.key ?? null;
  };

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const computePosition = () => {
      if (anchorRef?.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      } else {
        setPosition({ top: 56, right: 16 });
      }
    };
    computePosition();
    requestAnimationFrame(computePosition);
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown, true);
    };
  }, [open, onClose, anchorRef]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          className={`display-panel ${className}`.trim()}
          style={{ position: 'fixed', top: position?.top ?? 56, right: position?.right ?? 16 }}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.14 }}
        >
          {groupableColumns.length > 0 && (
            <>
              <div className="display-panel__section">
                <InputSelect
                  label="Grouping"
                  size="sm"
                  value={displaySettings.group.key ?? ''}
                  onChange={(e) =>
                    onUpdateGroup({ key: e.target.value || null })
                  }
                  options={[
                    { value: '', label: 'No Grouping' },
                    ...groupableColumns.map((col) => ({
                      value: col.key,
                      label: col.label,
                    })),
                  ]}
                />
              </div>

              <div className="display-panel__divider" />
            </>
          )}

          {!hideSortSection && (
            <>
              <div className="display-panel__section">
                <div className="display-panel__sort-row">
                  <div className="display-panel__sort-select">
                    <InputSelect
                      label="Ordering"
                      size="sm"
                      value={displaySettings.sort.key}
                      onChange={(e) =>
                        onUpdateSort({ ...displaySettings.sort, key: e.target.value })
                      }
                      options={sortableColumns.map((col) => ({
                        value: col.key,
                        label: col.label,
                      }))}
                    />
                  </div>
                  <button
                    className="display-panel__sort-dir"
                    onClick={() =>
                      onUpdateSort({
                        ...displaySettings.sort,
                        direction: displaySettings.sort.direction === 'asc' ? 'desc' : 'asc',
                      })
                    }
                    type="button"
                    aria-label={`Sort ${displaySettings.sort.direction === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={displaySettings.sort.direction}
                        className="display-panel__sort-dir-icon"
                        initial={
                          shouldReduceMotion
                            ? { opacity: 0 }
                            : { opacity: 0, transform: 'scale(0.6)', filter: 'blur(6px)' }
                        }
                        animate={
                          shouldReduceMotion
                            ? { opacity: 1 }
                            : { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }
                        }
                        exit={
                          shouldReduceMotion
                            ? { opacity: 0 }
                            : { opacity: 0, transform: 'scale(0.6)', filter: 'blur(6px)' }
                        }
                        transition={{ duration: 0.16, ease: [0.215, 0.61, 0.355, 1] }}
                      >
                        <Icon
                          name={
                            displaySettings.sort.direction === 'asc'
                              ? 'ArrowUpNarrowWide'
                              : 'ArrowDownNarrowWide'
                          }
                          size={16}
                        />
                      </motion.span>
                    </AnimatePresence>
                  </button>
                </div>
              </div>

              <div className="display-panel__divider" />
            </>
          )}

          {toggleOptions && toggleOptions.length > 0 && (
            <>
              <div className="display-panel__section">
                <span className="display-panel__section-title">Visibility</span>
                <div className="display-panel__columns">
                  {toggleOptions.map((opt) => (
                    <div key={opt.key} className="display-panel__column-row">
                      <span className="display-panel__column-label">{opt.label}</span>
                      <Toggle
                        checked={opt.checked}
                        onChange={() => onToggleOption?.(opt.key)}
                        ariaLabel={`Toggle ${opt.label}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="display-panel__divider" />
            </>
          )}

          <div className="display-panel__section">
            <span className="display-panel__section-title">{columnsSectionTitle}</span>
            <div className="display-panel__columns">
              {visibleColumns.map((col) => {
                const previousColumnKey = getMoveTarget(col.key, -1);
                const nextColumnKey = getMoveTarget(col.key, 1);
                const canReorder =
                  Boolean(onReorderColumn) &&
                  (reorderableSet ? reorderableSet.has(col.key) : true);

                return (
                <div key={col.key} className="display-panel__column-row">
                  <div className="display-panel__column-main">
                    {canReorder && (
                      <span className="display-panel__column-grip" aria-hidden="true">
                        <Icon name="GripVertical" size={14} />
                      </span>
                    )}
                    <span className="display-panel__column-label">{col.label}</span>
                  </div>
                  <div className="display-panel__column-actions">
                    {canReorder && (
                      <div className="display-panel__column-move-actions">
                        <button
                          type="button"
                          className="display-panel__column-move"
                          onClick={() => previousColumnKey && onReorderColumn?.(col.key, previousColumnKey)}
                          disabled={!previousColumnKey}
                          aria-label={`Move ${col.label} up`}
                        >
                          <Icon name="ChevronUp" size={13} />
                        </button>
                        <button
                          type="button"
                          className="display-panel__column-move"
                          onClick={() => nextColumnKey && onReorderColumn?.(col.key, nextColumnKey)}
                          disabled={!nextColumnKey}
                          aria-label={`Move ${col.label} down`}
                        >
                          <Icon name="ChevronDown" size={13} />
                        </button>
                      </div>
                    )}
                    <Toggle
                      checked={col.visible}
                      onChange={() => onToggleColumn(col.key)}
                      ariaLabel={`Toggle ${col.label} column`}
                    />
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <div className="display-panel__footer">
            <Button variant="secondary" size="md" onClick={onClose}>
              Close
            </Button>
            <div className="display-panel__footer-right">
              <Button variant="secondary" size="md" leadingVisual="RotateCcw" onClick={onReset}>
                Reset
              </Button>
              {isDefaultView ? (
                onSaveAsNew ? (
                  <Button variant="primary" size="md" leadingVisual="Save" onClick={() => { onSaveAsNew(); onClose(); }}>
                    Save as View
                  </Button>
                ) : (
                  <span title="Default views can't be modified. Save as a new view instead.">
                    <Button variant="primary" size="md" leadingVisual="Save" disabled>
                      Save
                    </Button>
                  </span>
                )
              ) : (
                <Button variant="primary" size="md" leadingVisual="Save" onClick={() => { onSave(); onClose(); }}>
                  Save
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
